import {
  CLOSURE_FACTOR,
  CONGESTION_EXPONENT, CONGESTION_MIN_SPEED, CONGESTION_SPACING,
  LANE_COST_FACTOR, LANE_UPKEEP_FACTOR, LOAD_TIME,
  TRUCK_CAPACITY, TRUCK_COST, TRUCK_SPEED, TRUCK_UPKEEP, UNLOAD_TIME,
} from "./constants";

/**
 * What a truck can move over a road of a given length. Pure geometry and
 * arithmetic — no world, no state, no events.
 *
 * It sits in its own module because two callers need it and they cannot import
 * each other: `sim.ts` runs it every frame, and `world.ts` needs `drainable()`
 * while `sim.ts` already imports `world.ts` for the map extensions. `sim.ts`
 * re-exports everything here, so no other file has to know this move happened.
 */

export function distance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Trucks a route carries before it jams. Always above 1, so the first truck a
 * player ever buys behaves exactly as it did before congestion existed — that
 * one is the tutorial.
 *
 * `lanes` multiplies rather than adding (RFC-0014 §3.1): a lane is a second copy
 * of the road, which is how the player reads it and what keeps the free first
 * truck a property of the road rather than of the whole route. The default of 1
 * is what makes lanes a safe change — every caller that predates RFC-0014, and
 * `drainable()` in particular, keeps its exact result.
 */
export const routeCapacity = (length: number, lanes = 1): number =>
  lanes * (1 + length / CONGESTION_SPACING);

/**
 * Speed multiplier for every truck on a route carrying `trucks` of them.
 *
 * Squared rather than linear so that the *total* the route moves has a maximum
 * at capacity and falls afterwards: a truck past that point does not merely
 * earn nothing, it takes throughput away from the ones that were working. That
 * is the second punishment for overcapacity which upkeep alone never delivered.
 *
 * It is a **local** maximum, and saying so is the whole of #123 — RFC-0003
 * §3.5.5 always carried the qualifier, four places that quoted it dropped it.
 * It fails on both sides. Below, because capacity is fractional and trucks are
 * not: the peak lands between two integers, so ceil(cap) trucks beat floor(cap)
 * wherever the fractional part clears about 0.35, which is 70.9 % of lengths
 * from 40 to 800 px. Above, because CONGESTION_MIN_SPEED pins the factor past
 * 2 × cap, so round-trip time stops growing and throughput climbs linearly
 * again — without bound, back over the local maximum at
 * `cap × (8L/TRUCK_SPEED + 2) / (2L/TRUCK_SPEED + 2)`, eleven trucks on the
 * 394 px route the tests calibrate against.
 *
 * Neither is a way to play: both cost trucks and upkeep, and RFC-0003 §3.5.6
 * requires the far side to stay unprofitable. Both shapes are pinned in
 * test/sim.test.ts so the next reader does not have to re-derive them.
 *
 * The `unlocked` default is true because the arithmetic has no business knowing
 * about milestones; step() passes the gate, and the panel and the renderer ask
 * the same question the player is looking at.
 */
export function congestionFactor(
  length: number, trucks: number, unlocked = true, lanes = 1,
): number {
  if (!unlocked) return 1;
  const cap = routeCapacity(length, lanes);
  if (trucks <= cap) return 1;
  return Math.max(CONGESTION_MIN_SPEED, (cap / trucks) ** CONGESTION_EXPONENT);
}

/**
 * Everything that scales a truck's speed on a route, in one place.
 *
 * There is exactly one reason this exists as a function rather than as a
 * multiplication at each call site: it already went wrong that way. RFC-0012
 * argued that a closure "reuses the congestion lever" and therefore shows up in
 * the panel and in `jamLevel()` for free — but the readouts call
 * `congestionFactor()`, not `step()`, so the closure reached the trucks and
 * nothing else. A closed route read 100 % while its trucks drove at half speed.
 *
 * `closed` rather than a factor, because the value is `CLOSURE_FACTOR` by
 * construction (`FACTOR` in `disruption.ts`) and a caller that could pass its own
 * number could pass a different one. Whether *this* route is the closed one is
 * `closedRoute()` in `sim.ts`, which needs the world.
 */
export const routeSpeed = (
  length: number, trucks: number, unlocked: boolean, lanes: number, closed: boolean,
): number => congestionFactor(length, trucks, unlocked, lanes) * (closed ? CLOSURE_FACTOR : 1);

/**
 * Sekunden für einen vollen Umlauf: hin, entladen, zurück, laden.
 *
 * The default of one truck is what keeps every pre-congestion caller correct:
 * congestionFactor(L, 1) is 1 for every L, so this returns what it always did.
 */
export const roundTripTime = (length: number, trucks = 1, lanes = 1): number =>
  roundTripAt(length, congestionFactor(length, trucks, true, lanes));

/**
 * The same trip, for a speed factor somebody else worked out.
 *
 * It exists because a route's factor is no longer a function of its own length
 * and fleet: since RFC-0033 it comes from the slowest **section**, and only the
 * caller knows which that is. Keeping the arithmetic here rather than at the
 * call site is architecture rule 4 — the panel showed a trip time it had
 * multiplied out itself for exactly one commit.
 */
export const roundTripAt = (length: number, factor: number): number =>
  (length / (TRUCK_SPEED * factor)) * 2 + LOAD_TIME + UNLOAD_TIME;

/** Units per second a fleet moves over a route running at this factor. */
export const throughputAt = (length: number, trucks: number, factor: number): number =>
  (trucks * TRUCK_CAPACITY) / roundTripAt(length, factor);

/** Theoretischer Durchsatz eines Lkw auf dieser Strecke, Einheiten/s. */
export const truckThroughput = (length: number, trucks = 1, lanes = 1): number =>
  TRUCK_CAPACITY / roundTripTime(length, trucks, lanes);

/**
 * Units per second the whole route moves. Local maximum at
 * `routeCapacity(length, lanes)` — there is no global one, see
 * `congestionFactor()`.
 */
export const routeThroughput = (length: number, trucks: number, lanes = 1): number =>
  trucks * truckThroughput(length, trucks, lanes);

/**
 * What one more lane costs to build, and what every lane past the first costs
 * per day.
 *
 * Priced in the truck-slots a lane opens, not in pixels (RFC-0014 §3.5). Per
 * pixel gets it backwards: the shortest roads open the fewest slots and would be
 * the cheapest to widen, and the shortest roads — the ones out of a hub — are
 * exactly the ones under pressure. Anchored on TRUCK_COST and TRUCK_UPKEEP
 * because the decision this creates is *lane against truck*, so a tuning pass on
 * the truck has to drag the lane with it.
 */
export const laneCost = (length: number): number =>
  Math.round(LANE_COST_FACTOR * TRUCK_COST * routeCapacity(length));

export const laneUpkeep = (length: number, lanes: number): number =>
  LANE_UPKEEP_FACTOR * TRUCK_UPKEEP * routeCapacity(length) * (lanes - 1);

/**
 * Units per second a route of this length moves at its own capacity — the most
 * any fleet can ever get off one producer over one road.
 *
 * It falls with length: a longer route holds more trucks but each one is
 * slower, and the product decays towards
 * TRUCK_CAPACITY · TRUCK_SPEED / (2 · CONGESTION_SPACING) = 1.9 u/s. That is
 * why a producer's rate has to depend on where it was placed (RFC-0005 §3.3):
 * a 2.4 u/s sawmill 500 px from every city could not be emptied by any fleet.
 *
 * Single-lane on purpose, and it takes no `lanes` argument so that it cannot
 * accidentally become anything else. This is the generator's question — "can a
 * player who buys nothing empty this producer?" — and the answer must not
 * improve because RFC-0014 exists.
 */
export const drainable = (length: number): number =>
  routeCapacity(length) * truckThroughput(length);
